import { sum } from 'es-toolkit/math';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { isRight } from 'fp-ts/Either';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';

const REVIEWS_PAGE_SIZE = 10;

export type RatingFilter = 1 | 2 | 3 | 4 | 5;
export type TimeFilter = 'day' | 'week' | 'month';
export type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';

export type ReviewFilters = {
  minRating: RatingFilter | null;
  timeWindow: TimeFilter | null;
};

const TIME_FILTER_MS: Record<TimeFilter, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

export type ReviewsPageData = {
  dappId: string;
  dappName: string;
  androidPackage: string;
  storeListingFound: boolean;
  canReply: boolean;
  firstReleaseDate: Date | null;
  summary: {
    rating: number | null;
    reviewsByRating: number[];
    replyCount: number | null;
  };
  reviews: ReviewRow[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    pageSize: number;
  };
};

export type ReviewRow = {
  id: string;
  createdAt: string;
  rating: number;
  review: string | null;
  walletAddress: string | null;
  domain: string | null;
  isReviewerAccountDeleted: boolean;
  publisherReply: PublisherReply | null;
};

export type PublisherReply = {
  id: string;
  review: string;
  createdAt: string;
  updatedAt: string;
  publisherName: string | null;
};

export type WeeklyDigest = {
  id: string;
  status: string;
  summary: string | null;
  windowStart: Date;
  windowEnd: Date;
  inputReviewCount: number;
  includedReviewCount: number;
  errorMessage: string | null;
};

export interface UseDashboardReviewsDataArgs {
  dappId: string | undefined;
}

export interface UseDashboardReviewsDataResult {
  canRender: boolean;
  isChecking: boolean;
  dappName: string | null;
  data: ReviewsPageData | null;
  displayData: ReviewsPageData | null;
  error: string | null;
  isLoading: boolean;
  digest: WeeklyDigest | null;
  isDigestLoading: boolean;
  /** From dapp analytics; null when unavailable (env-gated or no access). */
  latestVersionPercent: number | null;
  reviewsByRating: number[];
  totalRatings: number;
  totalPages: number;
  currentPage: number;
  isInitialLoad: boolean;
  hasError: boolean;
  showDigestPanel: boolean;
  storeListingMissing: boolean;
  filters: ReviewFilters;
  setFilters: (next: ReviewFilters) => void;
  sortBy: SortOption;
  setSortBy: (next: SortOption) => void;
  activeFilterCount: number;
  mutatingReviewId: string | null;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  handleRefresh: () => void;
  handleSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  handleDeleteReply: (review: ReviewRow) => Promise<void>;
}

export function useDashboardReviewsData({
  dappId,
}: UseDashboardReviewsDataArgs): UseDashboardReviewsDataResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const trpc = useTRPC();
  const { canRender, isChecking } = useRequireAuth();
  const { dappData } = useDappDataOptional();
  const [data, setData] = useState<ReviewsPageData | null>(null);
  const dappName = data?.dappName || dappData?.dappName || null;
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [digest, setDigest] = useState<WeeklyDigest | null>(null);
  const [isDigestLoading, setIsDigestLoading] = useState(false);
  const [latestVersionPercent, setLatestVersionPercent] = useState<
    number | null
  >(null);
  const pageParam = searchParams.get('page');
  const pageNumber = parsePageNumber(pageParam);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mutatingReviewId, setMutatingReviewId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReviewFilters>({
    minRating: null,
    timeWindow: null,
  });
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [now] = useState(() => Date.now());

  useEffect(() => {
    setData(null);
    setError(null);
    setDigest(null);
  }, [dappId]);

  useEffect(() => {
    if (pageParam === String(pageNumber)) {
      return;
    }

    setPageSearchParam({
      pageNumber,
      replace: true,
      setSearchParams,
    });
  }, [pageNumber, pageParam, setSearchParams]);

  useEffect(() => {
    // Normalize the URL before starting a request that navigation would discard.
    if (!canRender || !dappId || pageParam !== String(pageNumber)) {
      return;
    }

    let cancelled = false;
    setData(null);
    setIsLoading(true);
    setError(null);

    trpc.getDappRatingsReviews
      .query({
        dappId,
        first: REVIEWS_PAGE_SIZE,
        page: pageNumber,
        sortBy,
      })
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (result._tag === 'Left') {
          setError(result.left.message);
          setData(null);
          return;
        }

        setData({
          ...result.right,
          firstReleaseDate: result.right.firstReleaseDate
            ? toDate(result.right.firstReleaseDate)
            : null,
        });
        if (result.right.pagination.currentPage !== pageNumber) {
          setPageSearchParam({
            pageNumber: result.right.pagination.currentPage,
            replace: true,
            setSearchParams,
          });
        }
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }

        captureAndReportError(loadError, {
          component: 'DashboardReviews',
          operation: 'getDappRatingsReviews',
          dappId,
        });
        setError('Failed to load ratings and reviews');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    canRender,
    dappId,
    pageNumber,
    pageParam,
    refreshKey,
    setSearchParams,
    sortBy,
    trpc,
  ]);

  useEffect(() => {
    if (!canRender || !dappId) {
      setDigest(null);
      return;
    }

    let cancelled = false;
    setIsDigestLoading(true);

    trpc.getDappReviewWeeklyDigest
      .query({ dappId })
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (result._tag === 'Left') {
          setDigest(null);
          return;
        }

        const summary = result.right;
        if (!summary) {
          setDigest(null);
          return;
        }

        setDigest({
          id: summary.id,
          status: summary.status,
          summary: summary.summary,
          windowStart: toDate(summary.windowStart),
          windowEnd: toDate(summary.windowEnd),
          inputReviewCount: summary.inputReviewCount,
          includedReviewCount: summary.includedReviewCount,
          errorMessage: summary.errorMessage,
        });
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }

        captureAndReportError(loadError, {
          component: 'DashboardReviews',
          operation: 'getDappReviewWeeklyDigest',
          dappId,
        });
        setDigest(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsDigestLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canRender, dappId, refreshKey, trpc]);

  useEffect(() => {
    setLatestVersionPercent(null);
    if (!canRender || !dappId) {
      return;
    }

    let cancelled = false;
    void trpc.dappAnalytics.getDappAnalytics
      .query({ dappId, range: '7d' })
      .then((result) => {
        if (!cancelled && isRight(result)) {
          setLatestVersionPercent(
            result.right.activity.latestVersion7dPercent.value,
          );
        }
      })
      .catch(() => {
        // Analytics is env-gated; missing data is expected.
      });

    return () => {
      cancelled = true;
    };
  }, [canRender, dappId, refreshKey, trpc]);

  const reviewsByRating = data?.summary.reviewsByRating ?? [0, 0, 0, 0, 0];
  const totalRatings = sum(reviewsByRating);
  const totalPages =
    data?.pagination.totalPages ??
    Math.max(1, Math.ceil(totalRatings / REVIEWS_PAGE_SIZE));
  const currentPage = data?.pagination.currentPage ?? pageNumber;

  const isInitialLoad = isLoading && !data;
  const hasError = !!error && !data;
  const showDigestPanel = Boolean(digest?.summary || isDigestLoading);
  const storeListingMissing = data !== null && !data.storeListingFound;
  const activeFilterCount =
    (filters.minRating !== null ? 1 : 0) +
    (filters.timeWindow !== null ? 1 : 0);

  const filteredReviews = (() => {
    if (!data?.reviews) {
      return null;
    }

    const filtered = data.reviews.filter((review) => {
      if (filters.minRating !== null && review.rating < filters.minRating) {
        return false;
      }
      if (filters.timeWindow !== null) {
        const reviewTime = new Date(review.createdAt).getTime();
        if (
          Number.isNaN(reviewTime) ||
          now - reviewTime > TIME_FILTER_MS[filters.timeWindow]
        ) {
          return false;
        }
      }
      return true;
    });

    return filtered;
  })();

  const displayData: ReviewsPageData | null = (() => {
    if (!data || !filteredReviews) {
      return data;
    }
    return { ...data, reviews: filteredReviews };
  })();

  const handleNextPage = () => {
    if (!data?.pageInfo.hasNextPage) {
      return;
    }

    setPageSearchParam({
      pageNumber: currentPage + 1,
      setSearchParams,
    });
  };

  const handlePreviousPage = () => {
    if (currentPage <= 1) {
      return;
    }

    setPageSearchParam({
      pageNumber: currentPage - 1,
      setSearchParams,
    });
  };

  const handleRefresh = () => {
    setRefreshKey((previous) => previous + 1);
  };

  const updateReviewReply = (
    reviewId: string,
    publisherReply: PublisherReply | null,
  ) => {
    setData((previous) =>
      previous
        ? {
            ...previous,
            reviews: previous.reviews.map((review) =>
              review.id === reviewId ? { ...review, publisherReply } : review,
            ),
          }
        : previous,
    );
  };

  const handleSaveReply = async (review: ReviewRow, reply: string) => {
    if (!dappId) {
      return false;
    }

    const normalizedReply = reply.trim();
    if (normalizedReply.length === 0) {
      toast.error('Reply cannot be empty');
      return false;
    }
    if (normalizedReply.length > 500) {
      toast.error('Reply must be 500 characters or fewer');
      return false;
    }

    setMutatingReviewId(review.id);
    try {
      const result = await trpc.saveDappReviewReply.mutate({
        dappId,
        reviewId: review.id,
        reply: normalizedReply,
        expectedReplyUpdatedAt: review.publisherReply?.updatedAt,
      });

      if (result._tag === 'Left') {
        toast.error(toReplyToastMessage(result.left));
        return false;
      }

      updateReviewReply(review.id, result.right);
      toast.success('Publisher reply saved');
      return true;
    } catch (saveError) {
      captureAndReportError(saveError, {
        component: 'DashboardReviews',
        operation: 'saveDappReviewReply',
        dappId,
        reviewId: review.id,
      });
      toast.error('Failed to save publisher reply');
      return false;
    } finally {
      setMutatingReviewId(null);
    }
  };

  const handleDeleteReply = async (review: ReviewRow) => {
    if (!dappId || !review.publisherReply) {
      return;
    }

    if (!window.confirm('Delete this publisher reply?')) {
      return;
    }

    setMutatingReviewId(review.id);
    try {
      const result = await trpc.deleteDappReviewReply.mutate({
        dappId,
        reviewId: review.id,
        expectedReplyUpdatedAt: review.publisherReply.updatedAt,
      });

      if (result._tag === 'Left') {
        toast.error(toReplyToastMessage(result.left));
        return;
      }

      updateReviewReply(review.id, null);
      toast.success('Publisher reply deleted');
    } catch (deleteError) {
      captureAndReportError(deleteError, {
        component: 'DashboardReviews',
        operation: 'deleteDappReviewReply',
        dappId,
        reviewId: review.id,
      });
      toast.error('Failed to delete publisher reply');
    } finally {
      setMutatingReviewId(null);
    }
  };

  return {
    canRender,
    isChecking,
    dappName,
    data,
    displayData,
    error,
    isLoading,
    digest,
    isDigestLoading,
    latestVersionPercent,
    reviewsByRating,
    totalRatings,
    totalPages,
    currentPage,
    isInitialLoad,
    hasError,
    showDigestPanel,
    storeListingMissing,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    activeFilterCount,
    mutatingReviewId,
    handleNextPage,
    handlePreviousPage,
    handleRefresh,
    handleSaveReply,
    handleDeleteReply,
  };
}

function toReplyToastMessage(
  error: unknown,
  fallback = 'Failed to update publisher reply',
): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : fallback;

  if (
    message.includes('REPLY_CONFLICT') ||
    message.includes('VERSION_CONFLICT') ||
    message.includes('VERSION_REQUIRED')
  ) {
    return 'This review reply changed while you were editing. Refresh and try again.';
  }

  const normalizedMessage = message.toLowerCase();

  if (
    message.includes('REVIEW_NOT_FOUND') ||
    normalizedMessage.includes('review not found') ||
    normalizedMessage.includes('reply not found')
  ) {
    return 'This review is no longer available.';
  }

  if (
    message.includes('REVIEW_HAS_NO_TEXT') ||
    message.includes('Publisher replies require a written user review')
  ) {
    return 'You can only reply to written reviews.';
  }

  if (
    message.includes('REPLY_PRIVACY_VIOLATION') ||
    message.includes('PRIVACY_VIOLATION')
  ) {
    return 'Reply cannot include reviewer-identifying information.';
  }

  if (
    message.includes('REPLY_MODERATION_REJECTED') ||
    message.includes('MODERATION_REJECTED')
  ) {
    return 'Reply was rejected by review moderation.';
  }

  if (
    message.includes('REPLY_RATE_LIMITED') ||
    message.includes('RATE_LIMITED')
  ) {
    return 'Too many reply changes. Try again shortly.';
  }

  return fallback;
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function parsePageNumber(value: string | null): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return 1;
  }

  return parsed;
}

function setPageSearchParam({
  pageNumber,
  replace = false,
  setSearchParams,
}: {
  pageNumber: number;
  replace?: boolean;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}): void {
  const nextPageNumber = Math.max(1, pageNumber);
  const params = new URLSearchParams();
  params.set('page', String(nextPageNumber));

  setSearchParams(params, { replace });
}
